// src/pages/common/ConferenciaRascunhoItemsSidePanelContent.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft } from '@/icons';
import axios from 'axios';
import { apiGet, apiPost } from '../../services/api';
import { OrigemConferencia } from '../../constants';
import ScrollableTable from '../../components/common/ScrollableTable';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { formatDateTime, formatDate, getEmptyDetailsMessage } from '../../utils/formatUtils';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import DeleteButton from '../../components/common/DeleteButton';
import EditButton from '../../components/common/EditButton';
import SaveButton from '../../components/common/SaveButton';
import GiveUpButton from '../../components/common/GiveUpButton'
import Modal from '../../components/common/Modal';
import HistoricoItemModalContent from '../po/HistoricoItemModalContent';
import './ConferenciaRascunhoItemsSidePanelContent.css'; // CSS específico ou renomeado
import IconAddItemsButton from '../../components/common/IconAddItemsButton';
import SearchPendingReceiptProductModalContent, {
  type SelecaoTudoPendentes,
} from './SearchPendingReceiptProductModalContent';
import { ReceivedItem } from '../../types/recebimento/PendingReceiptItem';
import type { HistoricoResponse } from '../../types/compras/Historico';
import { ConferenciaRascunhoItem } from './Conferencia';
import { useApiCall } from '../../hooks/useApiCall';
import { useOperacaoLote } from '@/hooks/useOperacaoLote';
import { chaveLote, useOperacoesLote } from '@/contexts/OperacaoLoteContext';
import {
  contarSelecionados,
  fragmentoSelectAll,
  mensagemResultado,
  textoSelecao,
  type ModoSelecao,
} from '@/utils/selectAll';
import { showAlert, showConfirm } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

// Aba que esta tela reproduz. A listagem chama GET /{id}/itens SEM `status`, e o
// controller aplica defaultValue="RASCUNHO" — enviar o literal aqui é o que faz o
// select-all resolver exatamente o que a tela mostra. O backend ainda recusa o par
// (filtro.status, status da capa) divergente, gate compartilhado pelas duas ações
// (issue #64).
const STATUS_ABA = 'RASCUNHO';

interface ConferenciaRascunhoItemDetail {
  id: number;
  qtdCompra: number;
  qtdCompraExtra: number;
  qtdCompraOriginal: number;
  removeOrdemCompraItem: boolean;
  requisicaoItemId: number;
  ordemCompraId: number;
  codProduto: string;
  nomeProduto: string;
  codCategoriaProduto: number;
  nomeCategoria: string;
  requisicaoId: number;
  nomePessoaUsuario: string;
  prioridade: string;
  nomeFantasiaLoja: string;
  statusRecebimento: string;
  qtdReceber: number;
  qtdRecebidaTotal: number;
  ordemCompra: boolean;
  qtdRecebida: number;
  tipoRequisicao: string;
  contato: string;
  nomeCliente: string;
  nomeConsultor: string;
  origem: OrigemConferencia;
}

interface ConferenciaRascunhoDetails {
  dataCriacao: string;
  status: string;
  usuarioId: number;
  nomeUsuario: string;
  codFornecedor: number;
  nomeFornecedor: string;
  lojaEntregaId: number;
  nomeLojaEntrega: string;
  nroNotaFiscal: string;
  itens: {
    content: ConferenciaRascunhoItemDetail[];
    page: {
      size: number;
      number: number;
      totalElements: number;
      totalPages: number;
    };
  };
}

interface ConferenciaRascunhoItemsSidePanelContentProps {
  conferenciaRascunho: Partial<ConferenciaRascunhoItem> & { id: number }; // Alterado para Partial com id obrigatório; motivo: compatível com objeto parcial de Conferencia.tsx, resolvendo erro de tipo.
  theme: 'dark' | 'light';
  onClose: () => void;
  onPersistSuccess?: () => void;
}

const ConferenciaRascunhoItemsSidePanelContent: React.FC<ConferenciaRascunhoItemsSidePanelContentProps> = ({
  conferenciaRascunho,
  theme,
  onClose,
  onPersistSuccess,
}) => {
  const [header, setHeader] = useState<ConferenciaRascunhoDetails | null>(null);

  const fetchItems = useCallback(
    async (
      pageNumber: number,
      _currentItems: ConferenciaRascunhoItemDetail[]
    ): Promise<{ items: ConferenciaRascunhoItemDetail[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });
      const endpoint = `/v1/recebimento-conferencia/${conferenciaRascunho.id}/itens?${queryParams.toString()}`;
      const res = await apiGet(endpoint);
      const details = res.data;
      if (pageNumber === 0) {
        setHeader(details);
      }
      return { items: details.itens.content, totalItems: details.itens.page.totalElements };
    },
    [conferenciaRascunho.id]
  );

  const [selectedItems, setSelectedItems] = useState<ConferenciaRascunhoItemDetail[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedQuantities, setEditedQuantities] = useState<{ [key: number]: number }>({});
  const [isHistoricoModalOpen, setIsHistoricoModalOpen] = useState(false); // Estado adicionado para modal de histórico
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null); // Estado adicionado para ID do item selecionado
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null); // Estado adicionado para dados do histórico
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [nroNotaFiscal, setNroNotaFiscal] = useState(''); // Valor inicial vazio.
  const [showSaveNF, setShowSaveNF] = useState(false); // Controle para botão Salvar.
  const { callApi: callApiSave, isLoading: isSaving } = useApiCall();
  const { callApi: callApiNF, isLoading: isSavingNF } = useApiCall();
  const { callApi: callApiFinalize, isLoading: isFinalizing } = useApiCall();
  const { callApi: callApiDelete, isLoading: isDeleting } = useApiCall();
  const { callApi: callApiAddItems, isLoading: isAddingItems } = useApiCall();

  const { items, totalItems, hasMore, isLoading, loadMore, reset } = usePaginatedFetch<ConferenciaRascunhoItemDetail>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  // --- "Selecionar tudo entre páginas" (issue #31, superfícies 5 e 6) ---------
  // Diferente da cotação, aqui a prop NÃO é opt-in por modo: os checkboxes desta
  // tela servem a duas ações (Excluir e Editar quantidade) e as DUAS têm contrato
  // de select-all, sobre o MESMO conjunto — o backend resolve as duas com a mesma
  // query, justamente para não criar deriva (RecebimentoConferenciaItemService:329).
  // Um único `filtro` serve às duas.
  const [modoSelecao, setModoSelecao] = useState<ModoSelecao>('enumerado');
  const [excludeIds, setExcludeIds] = useState<Set<number>>(new Set());
  const { disparar, estaProcessando } = useOperacaoLote();
  const { aoConcluir } = useOperacoesLote();

  // Duas chaves, não uma. O índice único do backend é por (acao, chave_pai), e
  // EXCLUIR e EDITAR_QTD são valores distintos do enum AcaoLote — uma exclusão e
  // uma edição podem correr ao mesmo tempo na mesma conferência, e o Swagger da
  // rota diz isso em letra (RecebimentoConferenciaController:192).
  const chaveTravaExclusao = chaveLote('EXCLUIR', conferenciaRascunho.id);
  const chaveTravaEdicao = chaveLote('EDITAR_QTD', conferenciaRascunho.id);
  const chaveTravaAdicao = chaveLote('ADICIONAR', conferenciaRascunho.id);
  const processandoExclusao = estaProcessando(chaveTravaExclusao);
  const processandoEdicao = estaProcessando(chaveTravaEdicao);
  const processandoAdicao = estaProcessando(chaveTravaAdicao);
  const processandoLote = processandoExclusao || processandoEdicao || processandoAdicao;

  // O filtro que reproduz a listagem desta tela. Um só para as duas superfícies.
  const filtroConferencia = {
    recebimentoConferenciaId: conferenciaRascunho.id,
    status: STATUS_ABA,
  };

  // --- Seleção: convenção de checkbox de cabeçalho de tabela ------------------
  //   vazio   -> clicar marca a fila inteira (modo 'todos');
  //   traço   -> parte selecionada; clicar limpa;
  //   marcado -> tudo selecionado; clicar limpa.
  //
  // Esta tela NÃO passa renderCheckbox: todo item carregado é selecionável, então
  // não existe aqui o estado "bloqueado visível" que leva o cabeçalho a traço nas
  // telas de aprovação/reversão.

  const limparSelecao = () => {
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  const entrarEmTodos = () => {
    setModoSelecao('todos');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  const handleSelecionarItem = (item: ConferenciaRascunhoItemDetail, marcado: boolean) => {
    if (modoSelecao === 'todos') {
      // Desmarcar ALIMENTA a lista de exclusão — é o que permite dizer "todos
      // menos este" sem o navegador conhecer todos.
      setExcludeIds((prev) => {
        const next = new Set(prev);
        if (marcado) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
      return;
    }

    const proximos = marcado
      ? [...selectedItems, item]
      : selectedItems.filter((it) => it.id !== item.id);

    // Marcar à mão todas as linhas carregadas equivale a marcar o cabeçalho, e o
    // cabeçalho significa a fila inteira (regra do dev, superfície 1).
    if (marcado && items.length > 0 && proximos.length === items.length) {
      entrarEmTodos();
      return;
    }
    setSelectedItems(proximos);
  };

  const estadoCabecalho: 'vazio' | 'parcial' | 'marcado' =
    modoSelecao === 'todos'
      ? excludeIds.size === 0
        ? 'marcado'
        : 'parcial'
      : selectedItems.length === 0
        ? 'vazio'
        : 'parcial';

  const handleToggleCabecalho = () => {
    if (estadoCabecalho === 'vazio') entrarEmTodos();
    else limparSelecao();
  };

  // TETO, não promessa (ADR-030 item 3). O total vem da LISTAGEM; na edição o
  // servidor ainda descarta o item cujo teto é zero (a materialização), então o
  // processado pode ser menor. A mensagem de fim nunca repete este número.
  const qtdSelecionada = contarSelecionados(
    modoSelecao,
    totalItems,
    modoSelecao === 'enumerado' ? selectedItems.length : excludeIds.size,
  );

  const temSelecao = modoSelecao === 'todos' ? qtdSelecionada > 0 : selectedItems.length > 0;

  const handleDelete = async () => {
    if (modoSelecao === 'todos') {
      await dispararExclusaoSelectAll();
      return;
    }

    const ids = selectedItems.map(item => item.id);
    if (ids.length === 0 || !(await showConfirm('Tem certeza que deseja excluir os itens selecionados?'))) return;

    try {
      const queryParams = ids.map(id => `ids=${id}`).join('&');
      await callApiDelete('delete', `/v1/recebimento-conferencia/item?${queryParams}`, null, {
        onSuccess: () => {
          Toast.success('Item(s) excluídos com sucesso');
          setSelectedItems([]);
          setSelectionResetKey((k) => k + 1);
          reset();
        },
      });
    } catch (error) {
      logger.error('Erro ao excluir itens:', error);
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao excluir itens.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  // Fim do caminho INLINE da exclusão em lote. O assíncrono não passa por aqui:
  // quem avisa é o OperacaoLoteProvider, porque o usuário pode já ter saído.
  //
  // Sem contadores: o 200 devolve os ids efetivamente deletados, e no modo 'todos'
  // o front nunca soube quantos o servidor resolveu — não há denominador do qual
  // derivar "ignorados". `contadoresInline()` existe onde o corpo traz decisão por
  // item (aprovação, reversão), que não é o caso aqui.
  const finalizarExclusaoInline = () => {
    const msg = mensagemResultado({ assincrono: false });
    limparSelecao();
    setSelectionResetKey((k) => k + 1);
    // Avisa a lista-mãe, o que o caminho enumerado não faz. Não é inconsistência
    // por descuido: no modo 'todos' a exclusão pode esvaziar a conferência, e o
    // backend então a APAGA (cascata). Deixar o card no Kanban apontando para uma
    // conferência que não existe mais é pior que a assimetria com o enumerado,
    // que segue intocado.
    onPersistSuccess?.();
    reset();
    Toast.success(msg.texto);
  };

  const dispararExclusaoSelectAll = async () => {
    // A confirmação do modo enumerado não serve aqui: lá o usuário marcou linha a
    // linha e sabe o tamanho do que pediu; aqui um clique no cabeçalho pode
    // alcançar a fila inteira. E quando não há exclusão nenhuma o conjunto satura
    // a conferência — o backend então a apaga junto (cascata documentada no DTO).
    // Deixar isso acontecer em silêncio seria o oposto do que esta frente vem
    // fazendo; o precedente é o aviso de saturação da cotação (superfície 3).
    const confirmacao =
      excludeIds.size === 0
        ? `Excluir todos os itens desta conferência (até ${qtdSelecionada})? Sem itens, a conferência é removida.`
        : `Excluir os itens selecionados (até ${qtdSelecionada})?`;
    if (!(await showConfirm(confirmacao))) return;

    const payload = fragmentoSelectAll<typeof filtroConferencia, number>({
      filtro: filtroConferencia,
      excludeIds: [...excludeIds],
    });

    try {
      const resultado = await disparar({
        chaveTrava: chaveTravaExclusao,
        executar: () => apiPost('/v1/recebimento-conferencia/item/excluir', payload),
        onInline: finalizarExclusaoInline,
      });

      if (resultado === 'bloqueado') {
        await showAlert('Já existe uma exclusão em andamento nesta conferência.', {
          variant: 'warning',
        });
        return;
      }

      // Aceito para segundo plano: o provider já avisou e vai avisar de novo no
      // fim, onde quer que o usuário esteja. A lista NÃO é relida agora — quem
      // recarrega é a inscrição em aoConcluir, se o painel ainda estiver aberto.
      if (resultado === 'aceito') {
        limparSelecao();
        setSelectionResetKey((k) => k + 1);
      }
    } catch (error) {
      logger.error('Erro ao excluir itens:', error);
      // A mensagem do backend passa adiante: é ela que distingue "sem permissão"
      // (cobrada antes de resolver, issue #69) de "status da conferência não
      // corresponde ao solicitado" (issue #64).
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao excluir itens.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  // Recarrega a lista quando um lote DESTE pai termina, se o painel ainda estiver
  // aberto. O handler vive num ref para a inscrição não ser refeita a cada render.
  //
  // TRÊS ações, TRÊS handlers — e essa separação não é organização, é correção.
  // As três travas são independentes no backend, então qualquer uma pode concluir
  // enquanto o usuário está no meio de outra. Um handler único fecharia o modo de
  // edição e apagaria as quantidades digitadas quando o lote que terminou fosse o
  // de ADIÇÃO, que não tem relação nenhuma com o que ele está digitando. É a
  // mesma forma do defeito que a review da superfície 2 achou (um lote de uma
  // ação mexendo no estado de outra) e que reapareceu no lote A.
  //
  // O que TODAS têm em comum é só a lista: ela mudou, então recarrega e a seleção
  // pendente — que aponta para um conjunto que já não existe — é zerada.
  const recarregarAposLote = () => {
    limparSelecao();
    setSelectionResetKey((k) => k + 1);
    onPersistSuccess?.();
    reset();
  };

  const aoConcluirExclusaoRef = useRef<() => void>(() => {});
  aoConcluirExclusaoRef.current = () => {
    // Itens saíram. As quantidades digitadas para os que sobraram continuam
    // válidas, e o usuário segue no modo em que estava.
    recarregarAposLote();
  };

  const aoConcluirEdicaoRef = useRef<() => void>(() => {});
  aoConcluirEdicaoRef.current = () => {
    // Aqui sim: a edição que ele disparou terminou, então o modo se encerra e o
    // rascunho de quantidades deixa de fazer sentido.
    setEditMode(false);
    setEditedQuantities({});
    recarregarAposLote();
  };

  const aoConcluirAdicaoRef = useRef<() => void>(() => {});
  aoConcluirAdicaoRef.current = () => {
    // Itens novos entraram. NÃO se mexe no modo de edição nem no que o usuário
    // digitou — ele pode estar editando enquanto a adição roda em segundo plano.
    recarregarAposLote();
  };

  useEffect(
    () => aoConcluir(chaveTravaExclusao, () => aoConcluirExclusaoRef.current()),
    [aoConcluir, chaveTravaExclusao],
  );
  useEffect(
    () => aoConcluir(chaveTravaEdicao, () => aoConcluirEdicaoRef.current()),
    [aoConcluir, chaveTravaEdicao],
  );
  useEffect(
    () => aoConcluir(chaveTravaAdicao, () => aoConcluirAdicaoRef.current()),
    [aoConcluir, chaveTravaAdicao],
  );

  // Default por item da coluna "Qtd. Recebida" no modo de edição.
  //
  // No modo 'todos' o servidor NÃO usa a quantidade já recebida: ele aplica
  // `max(qtd_compra_original − SOMA(todas as rci), 0) + a própria`, que é a
  // semântica "selecionar tudo = receber o máximo de cada"
  // (RecebimentoConferenciaItemService:392-395). Esse teto é exatamente
  // `qtdReceber + qtdRecebida` — os dois já são colunas desta tela (a listagem
  // projeta ambos), então não há chamada nova nem cálculo replicado do servidor.
  //
  // Alinhar o exibido ao do servidor é a regra 2 da ADR-031: o dado gravado tem
  // de ser o dado que o usuário viu. Sem isso, uma linha não tocada mostraria a
  // quantidade atual e o banco receberia o teto.
  //
  // Uma função só, usada NO INPUT e NO PAYLOAD. Repetir a expressão permitiria
  // que uma linha não tocada MOSTRASSE um número e ENVIASSE outro.
  const quantidadeRecebidaPadrao = (item: ConferenciaRascunhoItemDetail): number =>
    modoSelecao === 'todos' ? item.qtdReceber + item.qtdRecebida : item.qtdRecebida;

  const handleEditClick = () => {
    setEditMode(true);
    // Deliberadamente SEM pré-preencher: `editedQuantities` passa a conter apenas
    // as linhas que o usuário tocou, que são as que viram `overrides`. Preencher
    // todas as linhas carregadas é a alternativa (C) que a ADR-031 rejeitou —
    // criaria assimetria invisível entre a linha visível e a que o usuário nunca
    // rolou, que continuaria no default do servidor.
    //
    // O modo enumerado fica byte-idêntico: o `?? quantidadeRecebidaPadrao(item)`
    // do input e do payload devolve `item.qtdRecebida`, o mesmo valor que o
    // pré-preenchimento gravava.
    setEditedQuantities({});
  };

  const handleGiveUp = () => {
    setEditMode(false);
    limparSelecao();
    setEditedQuantities({});
    setSelectionResetKey((k) => k + 1);
  };

  const normalizeQuantityInput = (rawValue: string) => {
    const normalized = rawValue.replace(',', '.');
    if (!normalized || normalized === '.') return 0;
    const [integerPartRaw, decimalPartRaw = ''] = normalized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const decimalPart = decimalPartRaw.slice(0, 3);
    const sanitized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const parsed = Number.parseFloat(sanitized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const handleQuantityChange = (id: number, value: string) => {
    const item = items.find(it => it.id === id);
    // No modo 'todos' o clamp é o teto real do servidor — o mesmo número que o
    // input já propõe. Digitar acima dele passaria pelo clamp histórico
    // (qtdCompraOriginal, mais frouxo), seria aceito no 202 e só derrubaria o
    // lote inteiro no laço de domínio, minutos depois e com a trava tomada.
    // No enumerado o clamp segue sendo qtdCompraOriginal, intocado.
    const max = item
      ? (modoSelecao === 'todos' ? quantidadeRecebidaPadrao(item) : item.qtdCompraOriginal)
      : 0;
    let num = normalizeQuantityInput(value);
    if (num > 0 && num < 0.001) num = 0.001;
    if (num > max) num = max;
    num = Number(num.toFixed(3));
    setEditedQuantities(prev => ({ ...prev, [id]: num }));
  };

  const handleSave = async () => {
    if (modoSelecao === 'todos') {
      await dispararEdicaoSelectAll();
      return;
    }

    if (selectedItems.length === 0) return;

    const payload = selectedItems.map(item => ({
      id: item.id,
      qtdRecebida: editedQuantities[item.id] ?? quantidadeRecebidaPadrao(item),
    }));

    try {
      await callApiSave('put', '/v1/recebimento-conferencia/item', payload, {
        onSuccess: () => {
          Toast.success('Quantidades recebidas atualizadas');
          onPersistSuccess?.();
          setEditMode(false);
          setSelectedItems([]);
          setEditedQuantities({});
          setSelectionResetKey((k) => k + 1);
          reset();
        },
      });
    } catch (error) {
      logger.error('Erro ao atualizar quantidades:', error);
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao editar quantidade recebida.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  // Linhas visíveis que o usuário editou à mão. Vão como `overrides`, aplicados
  // POR CIMA do default do servidor. O que ele digitou é o que grava; o que ele
  // não tocou fica com o default — que é o mesmo número que o input exibe, por
  // construção (quantidadeRecebidaPadrao alimenta os dois).
  //
  // A linha desmarcada é filtrada aqui também: um id em excludeIds não pode
  // voltar ao conjunto por vir num override. O backend já ignora esse caso, mas
  // mandá-lo declararia uma intenção que não existe.
  const overridesEditados = () =>
    items
      .filter((it) => !excludeIds.has(it.id) && editedQuantities[it.id] !== undefined)
      .map((it) => ({
        id: it.id,
        qtdRecebida: editedQuantities[it.id],
        // Só para os guards abaixo — removido antes de montar o corpo.
        teto: quantidadeRecebidaPadrao(it),
      }));

  // Fim do caminho INLINE da edição em lote. Sem contadores pelo mesmo motivo da
  // exclusão: o 200 devolve os itens efetivamente atualizados, e no modo 'todos'
  // o front nunca soube quantos o servidor resolveu — item de outro conferente é
  // pulado em silêncio e não aparece na resposta.
  const finalizarEdicaoInline = () => {
    const msg = mensagemResultado({ assincrono: false });
    limparSelecao();
    setEditMode(false);
    setEditedQuantities({});
    setSelectionResetKey((k) => k + 1);
    onPersistSuccess?.();
    reset();
    Toast.success(msg.texto);
  };

  const dispararEdicaoSelectAll = async () => {
    const overrides = overridesEditados();
    // O backend recusa override com quantidade não-positiva, mas só no laço de
    // domínio — e a ação é tudo-ou-nada, então uma linha zerada derrubaria o lote
    // inteiro depois do 202, com a trava já tomada. Barrar aqui é síncrono.
    //
    // Assimetria conhecida e ACEITA com o modo enumerado: lá o zero vai ao
    // servidor e volta 400, e o catch mostra a mensagem genérica — comportamento
    // histórico que esta frente não altera (premissa #9). O efeito observável é o
    // mesmo nos dois: a ação é recusada e nada é gravado.
    if (overrides.some((o) => o.qtdRecebida <= 0)) {
      await showAlert('Quantidade recebida deve ser maior que zero.', { variant: 'warning' });
      return;
    }

    // Uma quantidade digitada no modo enumerado SOBREVIVE à troca para 'todos' —
    // e deve mesmo sobreviver, descartá-la em silêncio é o defeito que a review
    // da superfície 1 achou. Mas o clamp de lá é `qtdCompraOriginal`, mais frouxo
    // que o teto real: o número digitado antes da troca pode estar acima do que
    // esta linha comporta. O laço de domínio só recusaria depois do 202.
    if (overrides.some((o) => o.qtdRecebida > o.teto)) {
      await showAlert(
        'Há quantidade acima do máximo a receber de algum item. Ajuste antes de salvar.',
        { variant: 'warning' },
      );
      return;
    }

    const payload = {
      ...fragmentoSelectAll<typeof filtroConferencia, number>({
        filtro: filtroConferencia,
        excludeIds: [...excludeIds],
      }),
      ...(overrides.length > 0
        ? { overrides: overrides.map(({ id, qtdRecebida }) => ({ id, qtdRecebida })) }
        : {}),
    };

    try {
      const resultado = await disparar({
        chaveTrava: chaveTravaEdicao,
        executar: () => apiPost('/v1/recebimento-conferencia/item/editar-qtd', payload),
        onInline: finalizarEdicaoInline,
      });

      if (resultado === 'bloqueado') {
        await showAlert('Já existe uma edição de quantidade em andamento nesta conferência.', {
          variant: 'warning',
        });
        return;
      }

      if (resultado === 'aceito') {
        limparSelecao();
        setEditMode(false);
        setEditedQuantities({});
        setSelectionResetKey((k) => k + 1);
      }
    } catch (error) {
      logger.error('Erro ao atualizar quantidades:', error);
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao editar quantidade recebida.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  const handleItemsAdded = async (newItems: ReceivedItem[]) => {
    if (newItems.length === 0) return;

    try {
      const queryParams = new URLSearchParams({
        tipo: 'ordemCompra',
        lojaEntregaId: header?.lojaEntregaId.toString() || '',
      });
      const endpoint = `/v1/recebimento-conferencia/${conferenciaRascunho.id}/itens?${queryParams.toString()}`;
      const body = newItems.map(item => ({
        ordemCompraItemId: item.ordemCompraRomaneioItemId,
        qtdRecebida: item.qtdRecebida,
      }));

      await callApiAddItems('post', endpoint, body, {
        onSuccess: () => {
          onPersistSuccess?.();
          reset();
          setShowSearchModal(false);
        },
      });
    } catch (error) {
      logger.error('Erro ao adicionar itens:', error);
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao adicionar itens.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  // Superfície 8: o modal devolve a INTENÇÃO (filtro + exclusões + overrides) e é
  // este pai que a traduz para a sua rota. O modal não faz POST nenhum — o mesmo
  // componente serve ao "Novo Recebimento", que monta um corpo diferente para
  // outra rota.
  const handleSelecionarTudoPendentes = async (selecao: SelecaoTudoPendentes) => {
    const payload = {
      ...fragmentoSelectAll<typeof selecao.filtro, number>({
        filtro: selecao.filtro,
        excludeIds: selecao.excludeIds,
      }),
      ...(selecao.overrides.length > 0 ? { overrides: selecao.overrides } : {}),
      tipo: 'ordemCompra',
      lojaEntregaId: header?.lojaEntregaId,
      // nroNotaFiscal fica FORA de propósito: o modo enumerado desta tela também
      // não o envia (o item novo nasce com NF nula e a NF é gravada depois, pelo
      // campo do cabeçalho). Mandá-lo aqui mudaria o dado persistido.
    };

    try {
      const resultado = await disparar({
        chaveTrava: chaveTravaAdicao,
        executar: () =>
          apiPost(
            `/v1/recebimento-conferencia/${conferenciaRascunho.id}/itens/adicionar`,
            payload,
          ),
        onInline: () => {
          const msg = mensagemResultado({ assincrono: false });
          onPersistSuccess?.();
          reset();
          Toast.success(msg.texto);
        },
      });

      if (resultado === 'bloqueado') {
        await showAlert('Já existe uma adição de itens em andamento nesta conferência.', {
          variant: 'warning',
        });
        return;
      }

      if (resultado === 'aceito') {
        onPersistSuccess?.();
      }
    } catch (error) {
      logger.error('Erro ao adicionar itens:', error);
      // A mensagem do backend passa adiante: é ela que distingue "divergência de
      // fornecedor" — que o filtro parcial (ILIKE) não consegue prever — de
      // "conferência fora de rascunho".
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao adicionar itens.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  const handleSaveNF = async () => {
    if (!nroNotaFiscal.trim()) return;

    try {
      await callApiNF('put', `/v1/recebimento-conferencia/${conferenciaRascunho.id}/nota-fiscal?nroNotaFiscal=${encodeURIComponent(nroNotaFiscal)}`, null, {
        onSuccess: () => {
          onPersistSuccess?.();
          setHeader(prev => prev ? { ...prev, nroNotaFiscal } : null);
          setShowSaveNF(false);
          Toast.success('Nota fiscal atualizada com sucesso.');
        },
      });
    } catch (error) {
      logger.error('Erro ao atualizar nota fiscal:', error);
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao atualizar nota fiscal.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  const handleFinalize = async () => {
    try {
      await callApiFinalize('put', `/v1/recebimento-conferencia/finalizar?recebimentoConferenciaId=${conferenciaRascunho.id}`, null, {
        onSuccess: () => {
          Toast.success('Conferência finalizada com sucesso.');
          onPersistSuccess?.();
          onClose();
        },
      });
    } catch (error) {
      logger.error('Erro ao finalizar conferência:', error);
      const msg = (axios.isAxiosError(error) && error.response?.data?.error) || 'Erro ao finalizar conferência.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  const handleViewHistoryClick = async (item: ConferenciaRascunhoItemDetail) => {
    try {
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        itemModuloId: item.requisicaoItemId.toString(),
        page: '0',
        size: '10',
      });
      const response = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
      setHistoricoData(response.data);
      setSelectedItemId(item.requisicaoItemId);
      setIsHistoricoModalOpen(true);
    } catch (error) {
      logger.error('Erro ao carregar histórico:', error);
      if (axios.isAxiosError(error) && error.response?.status === 400 && error.response?.data?.error) {
        await showAlert(error.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar histórico. Tente novamente.', { variant: 'error' });
      }
    }
  }; // Função adicionada para fetch e abertura do modal de histórico, adaptada do exemplo

  const handleCloseHistoricoModal = () => {
    setIsHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  }; // Função adicionada para fechar o modal de histórico

  useEffect(() => {
    reset();
  }, [conferenciaRascunho.id, reset]);

  useEffect(() => {
  if (header && header.nroNotaFiscal && /^\d+$/.test(header.nroNotaFiscal)) {
    setNroNotaFiscal(header.nroNotaFiscal); // Inicializa se numérico.
  }
  }, [header]); // Atualiza com header do GET.

  const baseColumns = [
    { header: 'Código', render: (item: ConferenciaRascunhoItemDetail) => item.codProduto },
    { header: 'Cód. Categoria', render: (item: ConferenciaRascunhoItemDetail) => item.codCategoriaProduto },
    { header: 'Nome do Produto', render: (item: ConferenciaRascunhoItemDetail) => item.nomeProduto },
    { header: 'Qtd. Total', render: (item: ConferenciaRascunhoItemDetail) => item.qtdCompraOriginal },
    { header: 'Requisição', render: (item: ConferenciaRascunhoItemDetail) => item.requisicaoId },
    { header: 'Ordem de Compra', render: (item: ConferenciaRascunhoItemDetail) => item.ordemCompraId },
    { header: 'Loja Solicitante', render: (item: ConferenciaRascunhoItemDetail) => item.nomeFantasiaLoja },
    { header: 'Requisitante', render: (item: ConferenciaRascunhoItemDetail) => item.nomePessoaUsuario },
    { header: 'Status Recebimento', render: (item: ConferenciaRascunhoItemDetail) => item.statusRecebimento },
    { header: 'Qtd. Receber', render: (item: ConferenciaRascunhoItemDetail) => item.qtdReceber },
    { header: 'Qtd. Total Recebido', render: (item: ConferenciaRascunhoItemDetail) => item.qtdRecebidaTotal },
    { header: 'Qtd. Recebida', render: (item: ConferenciaRascunhoItemDetail) => item.qtdRecebida },
  ]; // Colunas base separadas para adicionar coluna condicional

  const editColumns = editMode
    ? [
        {
          header: 'Qtd. Recebida',
          render: (item: ConferenciaRascunhoItemDetail) => (
            <input
              type="number"
              value={editedQuantities[item.id] ?? quantidadeRecebidaPadrao(item)}
              onChange={(e) => handleQuantityChange(item.id, e.target.value)}
              min={0.001}
              max={modoSelecao === 'todos' ? quantidadeRecebidaPadrao(item) : item.qtdCompraOriginal}
              step={0.001}
              className="quantity-input"
              disabled={processandoLote}
            />
          ),
          width: '100px',
        }, // Coluna com input para edição, adicionada condicionalmente
      ]
    : [];

  return (
    <div className={`conferencia-items-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel 
        onClick={onClose} 
        className="close-button-absolute"
        disabled={isLoading || isSaving || isSavingNF || isFinalizing || isDeleting || isAddingItems}
      />
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>Conferência Nº {conferenciaRascunho.id} | Criada em {header ? formatDateTime(header.dataCriacao) : 'Carregando...'}</h3>
          </div>
            <div className="facelet-p">
              <p>Conferente: {header?.nomeUsuario || 'Carregando...'} | Status: {header?.status || 'Carregando...'}</p>
              <p className='p-fornecedor'>
                Fornecedor: {header?.nomeFornecedor || 'Não informado'} | Loja de entrega: {header?.nomeLojaEntrega} | Nota Fiscal nº:&nbsp;
                <input
                  type="text"
                  value={nroNotaFiscal}
                  onChange={(e) => setNroNotaFiscal(e.target.value)}
                  placeholder="Insira o número"
                  className="nf-input"
                  onFocus={() => setShowSaveNF(nroNotaFiscal.length > 0)}
                  onBlur={() => setTimeout(() => setShowSaveNF(false), 200)} // Atraso de 200ms no hide para permitir clique antes do blur.
                  onKeyUp={() => setShowSaveNF(nroNotaFiscal.length > 0)}
                /> {/* Input para NF, mostra botão em focus com valor. */}
                {showSaveNF && (
                  <SaveButton 
                    isLoading={isSavingNF} 
                    onClick={handleSaveNF} 
                    className="save-nf-button" 
                    disabled={isSavingNF}
                  />
                )}
              </p>
            </div>
        </div>
        <div className='right-facelet'>
          <IconAddItemsButton
            onClick={() => setShowSearchModal(true)}
            title="Adicionar Itens"
            className="icon-box-open-button"
            disabled={isAddingItems}
          />
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <ScrollableTable<ConferenciaRascunhoItemDetail>
          data={items}
          hasMore={hasMore}
          loadMore={loadMore}
          isLoading={isLoading}
          columns={[...baseColumns, ...editColumns]}
          onSelectChange={setSelectedItems}
          selectionResetKey={selectionResetKey}
          renderHistoryAction={(item) => (
            <button
              className="action-button"
              onClick={() => handleViewHistoryClick(item)}
              title="Ver Histórico do Item"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </button>
          )}
          fetchDetails={async (item: ConferenciaRascunhoItemDetail) => {
            if (!item.tipoRequisicao) return []; // Evita chamada sem tipoRequisicao, retornando vazio.
            const res = await apiGet(`/v1/historico-itens/${item.requisicaoItemId}/po/requisicao-item/detalhes?tipoRequisicao=${item.tipoRequisicao}`);
            return res.data.content;
          }} // Adicionado: fetch para subtabela, replicando exemplo com requisicaoItemId.
          detailsColumns={[
            { header: 'Contato', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.contato ?? '') },
            { header: 'Cliente', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.nomeCliente ?? '') },
            { header: 'Consultor', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.nomeConsultor ?? '') },
            { header: 'Cotação', accessor: 'preOrdem' },
            { header: 'Ordem de Compra', accessor: 'ordemCompra' },
            { header: 'Qtd. Compra', accessor: 'qtdCompraOriginal' },
            { header: 'Qtd. Recebida', accessor: 'qtdRecebida' },
            { header: 'Status Recebimento', accessor: 'statusRecebimento' },
            { header: 'Comprador', accessor: 'comprador' },
            { header: 'Previsão Chegada', accessor: 'previsaoChegada', formatter: formatDate },
            { header: 'Conferência', accessor: 'recebimentoConferencia' },
            { header: 'Recebimento', accessor: 'dataConferencia', formatter: formatDateTime },
            { header: 'Conferente', accessor: 'conferente' },
            { header: 'Nº Nota Fiscal', accessor: 'nroNotaFiscal' },
          ]} // Adicionado: colunas para subtabela, replicando exemplo.
          emptyDetailsMessage={(item: ConferenciaRascunhoItemDetail) => getEmptyDetailsMessage(item.tipoRequisicao || '')} // Adicionado: mensagem vazia usando util centralizada.
          emptyMessage="Nenhum item encontrado."
          showCheckboxes={true}
          selecaoEntrePaginas={{
            estadoCabecalho,
            estaSelecionado: (it: ConferenciaRascunhoItemDetail) =>
              modoSelecao === 'todos'
                ? !excludeIds.has(it.id)
                : selectedItems.some((s) => s.id === it.id),
            onToggleCabecalho: handleToggleCabecalho,
            onSelecionarItem: handleSelecionarItem,
          }}
        />
      )}
      <div className="form-actions">
          {/* Escopo da seleção. Informativo — o gesto é o checkbox do cabeçalho. */}
          {estadoCabecalho !== 'vazio' && (
            <span className="conferencia-info-selecao">
              {textoSelecao(qtdSelecionada, totalItems)}
            </span>
          )}
          <DeleteButton
            onClick={handleDelete}
            disabled={!temSelecao || isDeleting || isSaving || isSavingNF || isFinalizing || isAddingItems || processandoLote}
          />
          {editMode ? (
            <>
              <GiveUpButton onClick={handleGiveUp} disabled={isSaving || processandoLote}/>
              {temSelecao && (
                <SaveButton
                  isLoading={isSaving || processandoEdicao}
                  onClick={handleSave}
                  disabled={isSaving || processandoLote}
                />
              )}
            </>
          ) : (
            <>
              <EditButton 
                onClick={handleEditClick} 
                disabled={isSaving || isSavingNF || isFinalizing || isDeleting || isAddingItems} 
              />
              <SaveButton 
                isLoading={isFinalizing} 
                onClick={handleFinalize} 
                label="Finalizar" 
                disabled={!header?.nroNotaFiscal || !/^\d+$/.test(header.nroNotaFiscal) || isFinalizing || isSavingNF}
              />
              {/* Motivo: desabilita "Finalizar" durante salvamento de NF, evitando ações conflitantes */}
            </>
          )}
        </div>
      {isHistoricoModalOpen && historicoData && (
        <Modal
          isOpen={isHistoricoModalOpen}
          onClose={handleCloseHistoricoModal}
          theme={theme}
          zIndex={1002}
        >
          <HistoricoItemModalContent
            historico={historicoData}
            requisicaoItemId={selectedItemId!}
            isOpen={isHistoricoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            showCancelButton={false}
          /> {/* Modal adicionado para histórico, similar ao exemplo */}
        </Modal>
      )}

      <Modal isOpen={showSearchModal} onClose={() => setShowSearchModal(false)} theme={theme}>
        <SearchPendingReceiptProductModalContent
          theme={theme}
          onClose={() => setShowSearchModal(false)}
          tipo="ordemCompra" // Fixo conforme endpoint.
          lojaId={header?.lojaEntregaId || null}
          onItemsAdded={handleItemsAdded} // Customizado para POST e update.
          onSelecionarTudo={handleSelecionarTudoPendentes}
        /> {/* Novo modal: usa componente compartilhado. Sem receivedItems: backend deduz qtdReceber via POST incremental. */}
      </Modal>
    </div>
  );
};

export default ConferenciaRascunhoItemsSidePanelContent;

